import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';

const bannerStyles = cssMap({
	banner: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.100'),
		transitionProperty: 'color, background-color',
		transitionDuration: '200ms',
	},
	bannerHovered: {
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	button: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		textAlign: 'left',
		backgroundColor: 'transparent',
		width: '100%',
	},
	contentStack: {
		flex: '1 1 0',
		minInlineSize: '0',
	},
});

export function SidenavBanner({
	onClick,
	onDismiss,
	icon,
	title,
	cta,
	bannerName,
	analyticsAttributes,
}: {
	onClick: () => void;
	onDismiss: () => void;
	icon: React.ReactNode;
	title: React.ReactNode;
	cta: React.ReactNode;
	bannerName: string;
	analyticsAttributes?: Record<string, unknown>;
}): JSX.Element {
	const hasFiredViewedRef = useRef(false);
	const { messageDismissed } = useMessageEvent();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const [isHovered, setIsHovered] = useState(false);

	const onMouseEnter = useCallback(() => {
		setIsHovered(true);
	}, []);

	const onMouseLeave = useCallback(() => {
		setIsHovered(false);
	}, []);

	const handleOnClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'bannerOpen',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: { actionType: 'open' },
			additionalMetadataFromFrontend: {},
		});
		onClick();
	}, [messageInstanceClicked, onClick]);

	const handleDismiss = useCallback(() => {
		void messageDismissed();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend: {},
		});
		onDismiss();
	}, [messageDismissed, messageInstanceClicked, onDismiss]);

	useEffect(() => {
		if (hasFiredViewedRef.current) return;
		hasFiredViewedRef.current = true;
		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: bannerName,
			attributes: analyticsAttributes,
		});
	}, [messageInstanceViewed, fireAnalyticsEvent, analyticsAttributes, bannerName]);

	return (
		<Flex
			xcss={cx(bannerStyles.banner, isHovered && bannerStyles.bannerHovered)}
			gap="space.200"
			justifyContent="space-between"
		>
			<Pressable
				xcss={bannerStyles.button}
				testId="jira-twcg-810-banner-cta"
				onClick={handleOnClick}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{icon}
				<Stack space="space.050" xcss={bannerStyles.contentStack}>
					<Text size="medium" weight="medium">
						{title}
					</Text>
					<Text size="medium" weight="medium" color="color.link">
						{cta}
					</Text>
				</Stack>
			</Pressable>
			<IconButton
				icon={CrossIcon}
				appearance="subtle"
				label="Close"
				isTooltipDisabled={false}
				onClick={handleDismiss}
				spacing="compact"
			/>
		</Flex>
	);
}
